import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, Calendar, Check, Mail, Users } from "lucide-react";
import ImageCarousel from "../components/ImageCarousel";
import ContactForm from "../components/ContactForm";

const activityPhotosReal = [
  { url: "/images/campamento/actividades/c1d37c99-87fe-4d50-9eac-4836e63b1ad8.jpg", caption: "Actividades" },
  { url: "/images/campamento/actividades/image00018.jpeg", caption: "Actividades" },
  { url: "/images/campamento/actividades/image00034.jpeg", caption: "Actividades" },
  { url: "/images/campamento/actividades/image00389.jpeg", caption: "Actividades" },
  { url: "/images/campamento/actividades/image01568.jpeg", caption: "Actividades" },
  { url: "/images/campamento/actividades/IMG_1898.jpg", caption: "Actividades" },
  { url: "/images/campamento/actividades/IMG_20250719_111818.jpg", caption: "Actividades" },
  { url: "/images/campamento/actividades/IMG_20250730_114416.jpg", caption: "Actividades" },
];

const facilityPhotosReal = [
  { url: "/images/campamento/instalaciones/Baño1.jpg", caption: "Baño" },
  { url: "/images/campamento/instalaciones/Comedor.jpg", caption: "Comedor" },
  { url: "/images/campamento/instalaciones/Duchas.jpg", caption: "Duchas" },
  { url: "/images/campamento/instalaciones/Exterior1.jpg", caption: "Exterior" },
  { url: "/images/campamento/instalaciones/Exterior2.jpg", caption: "Exterior" },
  { url: "/images/campamento/instalaciones/Exterior3.jpg", caption: "Exterior" },
  { url: "/images/campamento/instalaciones/Exterior4.jpg", caption: "Exterior" },
  { url: "/images/campamento/instalaciones/Exterior_5.jpg", caption: "Exterior" },
  { url: "/images/campamento/instalaciones/Habitaciones2.jpg", caption: "Habitaciones" },
  { url: "/images/campamento/instalaciones/Habitaciones_1.jpg", caption: "Habitaciones" },
  { url: "/images/campamento/instalaciones/Habitaciones_3.jpg", caption: "Habitaciones" },
  { url: "/images/campamento/instalaciones/Sala_multidisciplinar.jpg", caption: "Sala multidisciplinar" },
];

const includes = [
  "Alojamiento completo en albergue",
  "Manutención completa",
  "Actividades guiadas y talleres especializados",
  "Material didáctico y de actividades",
  "Seguro de accidentes y responsabilidad civil",
  "Equipo de monitores especializados en alta capacidad",
  "Acompañamiento emocional individualizado",
];

const metodologia = [
  {
    title: "Aprendizaje por proyectos",
    desc: "Los participantes trabajan en proyectos reales que combinan varias disciplinas, fomentando la profundidad y la creatividad.",
  },
  {
    title: "Grupos por afinidad",
    desc: "Se forman grupos según intereses y estilos cognitivos, no únicamente por edad, para maximizar la conexión.",
  },
  {
    title: "Espacio emocional seguro",
    desc: "Damos tanta importancia al bienestar emocional como al intelectual. Cada participante es acompañado de forma integral.",
  },
  {
    title: "Ratio bajo",
    desc: "Un máximo de 5 participantes por monitor garantiza atención real y personalizada en todo momento.",
  },
];

const faqItems = [
  {
    q: "¿Es necesario diagnóstico oficial?",
    a: "Sí. Para participar en el Campamento de Verano es necesario contar con un diagnóstico oficial de alta capacidad, emitido por un profesional cualificado. Si tienes dudas sobre qué informe o evaluación es válido, consúltanos.",
  },
  {
    q: "¿Pueden participar menores de 8 años?",
    a: "Por el momento el campamento está diseñado para participantes de 8 a 18 años. Estamos desarrollando programas para edades menores.",
  },
  {
    q: "¿Qué ocurre si mi hijo tiene dificultades de adaptación?",
    a: "Nuestro equipo está formado para gestionar estas situaciones. Los primeros días siempre incluyen actividades de cohesión y nuestros monitores hacen un seguimiento activo.",
  },
  {
    q: "¿Cómo sabré cuándo se abre la próxima edición?",
    a: "Puedes dejarnos tus datos en el formulario de aviso de esta página. Te escribiremos cuando publiquemos fechas, ubicación, programa y condiciones.",
  },
];

export default function CampamentoVerano() {
  return (
    <main data-testid="campamento-page" style={{ fontFamily: "Manrope, sans-serif" }}>
      <section className="relative pt-24 pb-20 px-5 lg:px-8" style={{ background: "#EBF5FB" }}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="absolute -top-20 right-0 opacity-[0.06]" width="500" height="500" viewBox="0 0 200 200">
            <polygon points="100,10 180,180 20,180" fill="#2B87BE" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="flex items-center gap-3 mb-6">
                <img src="/images/logos/campamento-dacpac.png" alt="DAC-PAC Campamento" className="h-10 w-10 object-contain" />
                <div className="section-label" style={{ color: "#2B87BE" }}>Campamento de verano</div>
              </div>
              <h1
                style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F", lineHeight: 1.15 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              >
                Campamento de verano
                <br />
                <em style={{ color: "#2B87BE" }}>para altas capacidades</em>
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                8 a 18 años. Días en la naturaleza donde ser curioso, intenso y diferente es exactamente lo que se espera de ti.
              </p>
              <p className="text-slate-500 text-sm leading-relaxed mb-8 max-w-2xl">
                La temporada de campamentos de verano ha finalizado. Estamos preparando la próxima edición y publicaremos fechas, ubicación, programa y condiciones cuando estén cerradas.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-[#D0E8F5]" style={{ color: "#0D3A4F" }}>
                  <Users size={14} style={{ color: "#2B87BE" }} /> 8-18 años
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-[#D0E8F5]" style={{ color: "#0D3A4F" }}>
                  <Calendar size={14} style={{ color: "#2B87BE" }} /> Próxima edición en preparación
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-[#D0E8F5]" style={{ color: "#0D3A4F" }}>
                  <Bell size={14} style={{ color: "#2B87BE" }} /> Aviso cuando se publique
                </span>
              </div>
              <div className="flex flex-wrap gap-4">
                <a href="#aviso-campamento" data-testid="campamento-aviso-cta" className="btn-primary">
                  Quiero que me aviséis <ArrowRight size={16} />
                </a>
                <Link to="/contacto" className="btn-outline">
                  Contactar
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <Link to="/equipo" className="text-[#1B5F7E] font-semibold hover:underline">
                  Conocer al equipo
                </Link>
                <Link to="/faq" className="text-[#1B5F7E] font-semibold hover:underline">
                  Ver preguntas frecuentes
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="rounded-3xl overflow-hidden shadow-xl"
              style={{ height: "440px" }}
            >
              <img src="/images/campamento/entorno-natural.jpg" alt="Naturaleza y campamento DAC-PAC" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      <section id="aviso-campamento" className="py-20 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="section-label mb-4" style={{ color: "#2B87BE" }}>Próxima edición</div>
              <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold mb-6">
                Te avisamos cuando publiquemos la información
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  La edición de verano ya ha terminado. En cuanto tengamos cerrados los detalles de la próxima temporada, enviaremos la información a las familias interesadas antes de actualizar el proceso de inscripción.
                </p>
                <p>
                  Déjanos tus datos y te escribiremos cuando estén disponibles las fechas, el lugar, el programa, el precio y el calendario de reserva.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl p-5 border border-[#D0E8F5]" style={{ background: "#EBF5FB" }}>
                  <Calendar size={20} style={{ color: "#2B87BE" }} className="mb-3" />
                  <h3 className="font-semibold text-sm mb-2" style={{ color: "#0D3A4F" }}>Sin fechas cerradas</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">Publicaremos el calendario cuando la próxima edición esté confirmada.</p>
                </div>
                <div className="rounded-2xl p-5 border border-[#D0E8F5]" style={{ background: "#FFFFFF" }}>
                  <Mail size={20} style={{ color: "#2B87BE" }} className="mb-3" />
                  <h3 className="font-semibold text-sm mb-2" style={{ color: "#0D3A4F" }}>Aviso por email</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">Usaremos este contacto para enviarte la información cuando se publique.</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-2xl p-6 md:p-8 border border-[#D0E8F5]"
              style={{ background: "#F8FAFC" }}
            >
              <ContactForm
                accentColor="#2B87BE"
                origen="aviso-campamento-verano"
                initialMensaje="Quiero recibir la información del próximo campamento de verano DAC-PAC cuando se publique."
                submitLabel="Solicitar aviso"
                successTitle="Solicitud recibida"
                successText="Te escribiremos cuando publiquemos la información del próximo campamento de verano."
                testPrefix="campamento-aviso"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="section-label mb-5" style={{ color: "#2B87BE" }}>Para quién es</div>
              <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold mb-6">
                Un campamento hecho
                <br />
                para mentes que van más allá
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  El Campamento DAC-PAC está diseñado para niños y jóvenes de 8 a 18 años con alta capacidad intelectual, creatividad desbordante, intensidad emocional y un enorme potencial por desarrollar en un entorno que estimule de verdad su talento.
                </p>
                <p>
                  Aquí encuentran un espacio donde profundizar en sus intereses, conectar con iguales, asumir retos a su altura y explorar todo lo que son capaces de hacer cuando se sienten comprendidos, estimulados y libres para pensar más allá.
                </p>
                <p>
                  <strong style={{ color: "#0D3A4F" }}>Se requiere diagnóstico oficial de alta capacidad.</strong> Si tienes dudas sobre qué evaluaciones son válidas, consúltanos.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl overflow-hidden" style={{ height: "220px" }}>
                <img src="/images/campamento/joven-campamento.jpg" alt="Joven en el campamento" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden mt-8" style={{ height: "220px" }}>
                <img src="/images/campamento/grupo-jovenes.jpg" alt="Grupo de jóvenes" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#F0F9FF" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="section-label mb-4" style={{ color: "#2B87BE" }}>Metodología</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold">
              Cómo vivimos el campamento
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {metodologia.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`metodologia-item-${i}`}
                className="p-7 rounded-2xl bg-white border border-[#D0E8F5]"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-4 text-white text-sm font-bold" style={{ background: "#2B87BE" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h4 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-xl font-semibold mb-3">{item.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="section-label mb-4" style={{ color: "#2B87BE" }}>Contenido</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold">
              ¿Qué incluye?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {includes.map((item) => (
              <div key={item} className="flex items-center gap-3 p-4 rounded-xl bg-[#EBF5FB]">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#2B87BE" }}>
                  <Check size={13} className="text-white" />
                </div>
                <span className="text-sm text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#EBF5FB" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="section-label mb-4" style={{ color: "#2B87BE" }}>Galería</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold">
              Así se vive el campamento
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: "#2B87BE" }}>Actividades</p>
              <ImageCarousel images={activityPhotosReal} height="340px" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: "#2B87BE" }}>Instalaciones</p>
              <ImageCarousel images={facilityPhotosReal} height="340px" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#F8FAFC" }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="section-label mb-4" style={{ color: "#2B87BE" }}>Preguntas frecuentes</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold">
              Dudas habituales
            </h2>
          </div>
          <div className="space-y-4">
            {faqItems.map((item) => (
              <div key={item.q} className="p-6 rounded-2xl bg-white border border-slate-100">
                <h4 style={{ fontFamily: "Manrope, sans-serif", color: "#0D3A4F", fontWeight: 600 }} className="mb-3">
                  {item.q}
                </h4>
                <p className="text-slate-500 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-5 lg:px-8" style={{ background: "#1A2B3C" }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 style={{ fontFamily: "Playfair Display, serif", color: "#FFFFFF" }} className="text-3xl md:text-4xl font-semibold mb-6">
            ¿Quieres recibir
            <br />
            la próxima información?
          </h2>
          <p className="text-[#B8D9E8] mb-10 leading-relaxed">
            Déjanos tus datos y te avisaremos cuando se publiquen fechas, condiciones y proceso de reserva.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#aviso-campamento" data-testid="campamento-final-cta" className="btn-primary">
              Solicitar aviso <ArrowRight size={16} />
            </a>
            <Link to="/equipo" style={{ color: "#B8D9E8" }} className="text-sm font-semibold self-center hover:underline">
              Ver equipo especializado
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
